import { MutableRefObject, useContext } from "react";
import { Controller } from "react-hook-form";
import { Alert, Button, Col, Form, Input, Row, Select, Upload } from "antd";
import Text from "antd/es/typography/Text";
import {
    CheckOutlined,
    CloseOutlined,
    EditOutlined,
    GiftOutlined,
    PlusOutlined,
    TrophyOutlined,
    WarningOutlined,
} from "@ant-design/icons";

import { IGiveawayAuto } from "../../../../../../interfaces";
import { ThemeContext } from "../../../../../../contexts/ThemeContext";
import useAwardsStep from "./useAwardsStep";
import UploadButton from "./components/UploadButton";
import AwardsList from "./components/AwardsList";
import { awardImagesUrl } from "../../../../../../services/urls";
import SimpleModal from "../../../../../../components/SimpleModal";
import { removeAcento } from "../../../../../../utils/RemoveAcentos";
import { formatCurrencyInput } from "../../../../../../utils/Masks";

interface IProps {
    giveawayAuto?: IGiveawayAuto;
    onAwardsListLengthChange: (length: number) => void;
    refIsEditing: MutableRefObject<boolean>;
    canEdit: boolean;
}

export default function AwardsStep({
    giveawayAuto,
    onAwardsListLengthChange,
    refIsEditing,
    canEdit,
}: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const {
        handleSubmit,
        handleSubmitForm,
        control,
        errors,
        isSubmiting,
        isLoadingAwardTypes,
        orderedAwardTypes,
        awardBeingEdited,
        fileList,
        dummyRequest,
        handleFileListChange,
        selectedTypeDefaultImageUrl,
        awards,
        handleClickEditAward,
        handleClickDeleteAward,
        handleCancelEditAward,
        awardBeingEditedImageIsDefault,
        isDeleteModalVisible,
        handleConfirmDeleteAward,
        handleCancelDeleteAward,
        awardBeingDeleted,
        isLoadingDelete,
        isConfirmOverwriteFieldsToEditModalVisible,
        isConfirmFinishModalVisible,
        handleCancelOverwriteFields,
        handleConfirmOverwriteFields,
        handleClickFinish,
        handleCancelFinish,
        handleConfirmFinish,
        handleUpdateAwardsOrder,
        isOrderUnsaved,
        handleOrderChanged,
        ACCEPTED_IMAGE_TYPES,
    } = useAwardsStep(giveawayAuto, onAwardsListLengthChange);

    const giveawayEnded = refIsEditing.current && !canEdit;

    if (!giveawayAuto) {
        return null;
    }

    const sectionTitleStyle: React.CSSProperties = {
        fontSize: 13,
        fontWeight: 600,
        color: isDark ? '#94a3b8' : '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        marginBottom: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    };

    const cardStyle = {
        background: isDark ? '#141414' : '#ffffff',
        borderRadius: 10,
        border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
        boxShadow: isDark
            ? '0 4px 20px rgba(0, 0, 0, 0.35)'
            : '0 2px 10px rgba(0, 0, 0, 0.03)',
        padding: '24px',
        height: '100%',
    };

    return (
        <Form
            onFinish={handleSubmit(handleSubmitForm)}
            autoComplete="off"
            layout="vertical"
        >
            <Row gutter={[16, 16]}>
                {/* Coluna da Esquerda: Formulário de Adicionar / Editar Prêmio */}
                <Col xs={24} lg={10}>
                    <div style={cardStyle}>
                        <div style={sectionTitleStyle}>
                            <GiftOutlined style={{ color: '#1677ff' }} />
                            <span>
                                {awardBeingEdited && !isConfirmOverwriteFieldsToEditModalVisible
                                    ? 'Editar Prêmio'
                                    : 'Cadastrar Prêmio'}
                            </span>
                        </div>

                        <Form.Item
                            label="Descrição do Prêmio"
                            validateStatus={errors.description ? "error" : undefined}
                            help={errors.description?.message}
                        >
                            <Controller
                                name="description"
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: "Por favor, digite a descrição do prêmio!",
                                    },
                                }}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        prefix={<EditOutlined style={{ color: isDark ? '#666' : '#bfbfbf' }} />}
                                        placeholder="Ex: Moto 0km / Smart TV 55 polegadas"
                                        disabled={giveawayEnded}
                                    />
                                )}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Tipo do Prêmio"
                            validateStatus={errors.type_id ? "error" : undefined}
                            help={errors.type_id?.message}
                        >
                            <Controller
                                name="type_id"
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: "Por favor, informe o tipo do prêmio!",
                                    },
                                }}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={orderedAwardTypes.map((type) => ({
                                            value: type.id,
                                            label: type.name,
                                        }))}
                                        placeholder="Selecione o tipo do prêmio"
                                        disabled={isSubmiting || giveawayEnded || isLoadingAwardTypes}
                                        loading={isLoadingAwardTypes}
                                        showSearch
                                        filterOption={(input, option) =>
                                            removeAcento(option?.label ?? "").toLowerCase().includes(removeAcento(input).toLowerCase())
                                        }
                                        style={{ width: '100%' }}
                                    />
                                )}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Valor Estimado (R$)"
                            validateStatus={errors.value ? "error" : undefined}
                            help={errors.value?.message}
                        >
                            <Controller
                                name="value"
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: "Por favor, informe o valor do prêmio!",
                                    },
                                }}
                                render={({ field: { onChange, ...field } }) => (
                                    <Input
                                        {...field}
                                        onChange={(e) => onChange(formatCurrencyInput(e.target.value))}
                                        addonBefore="R$"
                                        placeholder="0,00"
                                        style={{ width: "100%" }}
                                        disabled={giveawayEnded}
                                    />
                                )}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Imagem Ilustrativa"
                            validateStatus={errors.images ? "error" : undefined}
                            help={errors.images?.message}
                            extra={
                                <span style={{ fontSize: 12, color: isDark ? '#8c8c8c' : '#64748b' }}>
                                    Formatos aceitos: JPG, PNG, WEBP.
                                </span>
                            }
                        >
                            <Controller
                                name="images"
                                control={control}
                                render={({ field: { value, onChange, ...field } }) => (
                                    <Upload
                                        {...field}
                                        listType="picture-card"
                                        fileList={fileList}
                                        customRequest={dummyRequest}
                                        onChange={handleFileListChange}
                                        accept={ACCEPTED_IMAGE_TYPES.toString()}
                                        disabled={giveawayEnded}
                                        style={{ width: '100%' }}
                                    >
                                        {fileList && fileList.length >= 1 ? null : awardBeingEdited?.images &&
                                            awardBeingEdited.images.length > 0 &&
                                            !awardBeingEditedImageIsDefault &&
                                            !isConfirmOverwriteFieldsToEditModalVisible ? (
                                            <img
                                                src={`${awardImagesUrl}/${awardBeingEdited?.images[0].img}`}
                                                alt="award"
                                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                                            />
                                        ) : selectedTypeDefaultImageUrl ? (
                                            <img
                                                src={selectedTypeDefaultImageUrl}
                                                alt="award"
                                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                                            />
                                        ) : (
                                            <UploadButton />
                                        )}
                                    </Upload>
                                )}
                            />
                        </Form.Item>

                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                gap: 10,
                                marginTop: 12,
                            }}
                        >
                            {awardBeingEdited && !isConfirmOverwriteFieldsToEditModalVisible && (
                                <Button
                                    htmlType="button"
                                    icon={<CloseOutlined />}
                                    onClick={handleCancelEditAward}
                                    disabled={isSubmiting}
                                    style={{
                                        borderRadius: 8,
                                        height: 38,
                                        fontWeight: 500,
                                    }}
                                >
                                    Cancelar edição
                                </Button>
                            )}
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={
                                    awardBeingEdited && !isConfirmOverwriteFieldsToEditModalVisible ? (
                                        <CheckOutlined />
                                    ) : (
                                        <PlusOutlined />
                                    )
                                }
                                loading={isSubmiting}
                                disabled={isSubmiting || isOrderUnsaved || giveawayEnded}
                                style={{
                                    borderRadius: 8,
                                    height: 38,
                                    fontWeight: 600,
                                }}
                            >
                                {awardBeingEdited && !isConfirmOverwriteFieldsToEditModalVisible
                                    ? "Salvar alterações"
                                    : "Adicionar prêmio"}
                            </Button>
                        </div>

                        {isOrderUnsaved && (
                            <Alert
                                type="warning"
                                showIcon
                                icon={<WarningOutlined />}
                                message="Ordenação não salva"
                                description="Salve ou cancele a reordenação dos prêmios na tabela ao lado antes de adicionar ou editar itens."
                                style={{ marginTop: 14, borderRadius: 8 }}
                            />
                        )}
                    </div>
                </Col>

                {/* Coluna da Direita: Lista de Prêmios Cadastrados */}
                <Col xs={24} lg={14}>
                    <AwardsList
                        awards={awards}
                        onClickEditAward={handleClickEditAward}
                        onClickDeleteAward={handleClickDeleteAward}
                        onUpdateAwardsOrder={handleUpdateAwardsOrder}
                        onAwardsOrderChanged={handleOrderChanged}
                        giveawayEnded={giveawayEnded}
                    />
                </Col>
            </Row>

            {/* Barra de Ações Inferior: Finalizar */}
            <div
                style={{
                    background: isDark ? '#141414' : '#ffffff',
                    borderRadius: 10,
                    border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                    boxShadow: isDark
                        ? '0 4px 20px rgba(0, 0, 0, 0.35)'
                        : '0 2px 10px rgba(0, 0, 0, 0.03)',
                    padding: '16px 24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    marginTop: 12,
                }}
            >
                <Button
                    type="primary"
                    htmlType="button"
                    icon={<TrophyOutlined />}
                    disabled={isSubmiting}
                    onClick={handleClickFinish}
                    style={{
                        height: 38,
                        borderRadius: 8,
                        fontWeight: 600,
                        padding: '0 24px',
                        background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                        borderColor: '#52c41a',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                    }}
                >
                    Finalizar Cadastro do Sorteio
                </Button>
            </div>

            <SimpleModal
                open={isDeleteModalVisible}
                onOk={handleConfirmDeleteAward}
                okButtonProps={{
                    disabled: isLoadingDelete,
                    loading: isLoadingDelete,
                    danger: true,
                }}
                cancelButtonProps={{
                    disabled: isLoadingDelete,
                }}
                okText="Excluir Prêmio"
                onCancel={handleCancelDeleteAward}
                title={`Deseja realmente excluir o prêmio "${awardBeingDeleted?.description}"?`}
            />

            <SimpleModal
                open={isConfirmOverwriteFieldsToEditModalVisible}
                onOk={handleConfirmOverwriteFields}
                onCancel={handleCancelOverwriteFields}
                title="Substituir dados do formulário?"
            >
                <Text>
                    O seu formulário possui campos preenchidos. Ao selecionar outro prêmio para editar, os dados preenchidos serão substituídos. Deseja continuar?
                </Text>
            </SimpleModal>

            <SimpleModal
                open={isConfirmFinishModalVisible}
                onOk={handleConfirmFinish}
                onCancel={handleCancelFinish}
                title="Existem dados não salvos no formulário!"
            >
                <Text>
                    Você possui informações preenchidas no prêmio que ainda não foram salvas. Clique em "Adicionar prêmio" caso queira incluí-lo, ou clique em <strong style={{ color: "#ff4d4f" }}>Confirmar</strong> para finalizar sem salvar o prêmio atual.
                </Text>
            </SimpleModal>
        </Form>
    );
}
